import {useState} from 'react';

import {swap} from '@/api/transactions';
import currencies from '@/constants/currencies';
import {useAppRates} from '@/context/AppRatesContext';
import type {ICurrency} from '@/types/Currency';
import {
  convertCurrency,
  convertToSourceCurrency,
} from '@/utils/currencyConverter';
import {getErrorMessage} from '@/utils/errorMessage';

export type SwapResponse = {
  // '' while nothing has been attempted yet; drives the CustomToast variant.
  status: '' | 'success' | 'error';
  message: string;
};

const useSwap = (
  userId: string,
  initialAmount: NonNullable<unknown>,
  initialSwapResponse: SwapResponse,
) => {
  const [amount, setAmount] = useState<{[key: string]: number}>(initialAmount);
  const [currency, setCurrency] = useState<{[key: string]: ICurrency}>({
    from: currencies[0],
    to: currencies[1],
  });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [swapResponse, setSwapResponse] = useState(initialSwapResponse);
  const {state} = useAppRates();
  const rates = state.appRates;

  const handleAmountChange = (name: string, value: number) => {
    // onChange of one input, the equivalent should be shown in the other input

    // so if the person types in the to box, the input to convert should be the from
    const inputToConvert = name === 'from' ? 'to' : 'from';

    // the swap converts from to to along the from to to rate, so the "to" box
    // is turned back into "from" by dividing by it, never by the to to from rate
    const conversion = {
      from: currency.from.code,
      to: currency.to.code,
      value,
      rates,
    };
    const convertedValue =
      name === 'from'
        ? convertCurrency(conversion)
        : convertToSourceCurrency(conversion);
    setAmount({[name]: value, [inputToConvert]: convertedValue});
  };

  const handleCurrencyChange = (name: string, value: ICurrency) => {
    setCurrency({...currency, [name]: value});
    // so if the user changes the currency the equivalent should be shown in the other input field
    const convertedValue =
      name === 'from'
        ? convertToSourceCurrency({
            from: value.code,
            to: currency.to.code,
            value: amount.to,
            rates,
          })
        : convertCurrency({
            from: currency.from.code,
            to: value.code,
            value: amount.from,
            rates,
          });
    setAmount({...amount, [name]: convertedValue});
  };

  async function handleSwap() {
    try {
      setLoading(true);
      const response = await swap({
        fromCurrency: currency.from.code,
        toCurrency: currency.to.code,
        amount: amount.from,
        owner: userId,
      });
      setLoading(false);
      setShowModal(false);

      // POST /v2/admin/transactions/swap answers with the created transaction,
      // so an empty body means the swap was not confirmed. Report that as an
      // error rather than telling an admin money moved: the amount is left
      // in the form so the swap can be retried.
      if (response === null || response === undefined) {
        setSwapResponse({
          status: 'error',
          message: 'Swap could not be confirmed. Contact Admin',
        });
        return;
      }

      // fetchUserAssets();
      setAmount(initialAmount);
      setSwapResponse({
        status: 'success',
        message: `Successfully swapped ${amount.from} ${currency.from.code} to ${amount.to} ${currency.to.code}`,
      });
    } catch (err: unknown) {
      setLoading(false);
      setShowModal(false);
      const message = getErrorMessage(err, 'Unknown Error! Contact Admin');
      setSwapResponse({status: 'error', message});
    }
  }

  // Check that value is a number and its greater than 0
  // Also check that the from and to currency are not same
  function isSwapButtonDisabled() {
    return (
      currency.from.code === currency.to.code ||
      amount.from <= 0 ||
      isNaN(amount.from) ||
      amount.to <= 0 ||
      isNaN(amount.to)
    );
  }

  return {
    rates,
    amount,
    currency,
    loading,
    showModal,
    swapResponse,
    handleAmountChange,
    handleCurrencyChange,
    handleSwap,
    isSwapButtonDisabled,
    setShowModal,
    setSwapResponse,
  };
};

export default useSwap;
